import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Loader2, MapPin, Calendar, Home, AlertCircle, Sparkles } from "lucide-react";
import { ClientPreferencesForm } from "@/components/ClientPreferencesForm";

interface Placement {
  id: string;
  property_id: string;
  start_date: string;
  end_date: string | null;
  status: string;
  notes: string | null;
  properties: {
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    bedrooms: number;
    bathrooms: number;
    rent_amount: number;
  };
}

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { impersonatedUser, isImpersonating } = useImpersonation();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [activePlacement, setActivePlacement] = useState<Placement | null>(null);
  const [matchedProperties, setMatchedProperties] = useState<any[]>([]);

  const effectiveUserId = isImpersonating ? impersonatedUser?.id : user?.id;

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (effectiveUserId) {
      fetchMatchedProperties();
    }
  }, [effectiveUserId]);

  useEffect(() => {
    if (effectiveUserId) {
      fetchPlacements();
    }
  }, [effectiveUserId]);

  // Real-time subscription for placement updates
  useEffect(() => {
    if (!effectiveUserId) return;

    const channel = supabase
      .channel('placement-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'placements',
          filter: `client_id=eq.${effectiveUserId}`
        },
        (payload) => {
          console.log('Placement updated:', payload);
          if (payload.old.status !== payload.new.status) {
            toast.info(`Your placement status has been updated to ${payload.new.status}`);
          }
          fetchPlacements();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [effectiveUserId]);

  const fetchMatchedProperties = async () => {
    if (!effectiveUserId) return;

    try {
      const { data: profile, error: profileError } = await supabase
        .from("client_profiles")
        .select("desired_bedrooms, desired_bathrooms, max_rent, preferred_cities, preferred_states")
        .eq("user_id", effectiveUserId)
        .maybeSingle();

      if (profileError) throw profileError;
      if (!profile) return;

      // Fetch available properties
      const { data: properties, error: propertiesError } = await supabase
        .from("properties")
        .select("*")
        .eq("status", "available");

      if (propertiesError) throw propertiesError;

      // Filter and score properties
      const matches = (properties || [])
        .filter(property => {
          if (profile.desired_bedrooms && Math.abs(property.bedrooms - profile.desired_bedrooms) > 1) return false;
          if (profile.max_rent && property.rent_amount > profile.max_rent) return false;
          return true;
        })
        .map(property => {
          let score = 0;
          if (profile.desired_bedrooms && property.bedrooms === profile.desired_bedrooms) score += 3;
          if (profile.desired_bathrooms && property.bathrooms >= profile.desired_bathrooms) score += 2;
          if (profile.max_rent && property.rent_amount <= profile.max_rent) score += 3;
          if (profile.preferred_cities) {
            const cities = profile.preferred_cities.split(',').map(c => c.trim().toLowerCase());
            if (cities.includes(property.city.toLowerCase())) score += 2;
          }
          return { ...property, matchScore: score };
        })
        .sort((a, b) => b.matchScore - a.matchScore)
        .slice(0, 3);

      setMatchedProperties(matches);
    } catch (error: any) {
      console.error("Error fetching matched properties:", error);
    }
  };

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        navigate("/auth");
        return;
      }

      setUser(session.user);

      // Skip role check when admin is impersonating
      if (!isImpersonating) {
        const { data: roleData } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", session.user.id)
          .eq("role", "client")
          .maybeSingle();

        if (!roleData) {
          toast.error("Access denied. Client privileges required.");
          navigate("/dashboard");
          return;
        }
      }
    } catch (error: any) {
      console.error("Error checking auth:", error);
      toast.error("Authentication error");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlacements = async () => {
    if (!effectiveUserId) return;

    try {
      const { data, error } = await supabase
        .from("placements")
        .select(`
          *,
          properties(*)
        `)
        .eq("client_id", effectiveUserId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPlacements(data || []);
      
      const active = data?.find(p => p.status === "active");
      setActivePlacement(active || null);
    } catch (error: any) {
      console.error("Error fetching placements:", error);
      toast.error("Failed to load placement information");
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "outline"> = {
      pending: "outline",
      active: "default",
      completed: "secondary"
    };
    return variants[status] || "outline";
  };

  const getStatusLabel = (status: string) => {
    if (status === "pending") return "Pending";
    if (status === "completed") return "Closed";
    return "Active";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-1 container mx-auto px-4 py-8 mt-20">
        <div className="max-w-6xl mx-auto space-y-8">
          <div>
            <h1 className="text-4xl font-bold flex items-center gap-2">
              <Home className="h-8 w-8" />
              Client Dashboard
            </h1>
            <p className="text-muted-foreground mt-2">
              View your placement information and housing details
            </p>
          </div>

          {/* Stats Overview */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Current Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {activePlacement ? "Active" : "No Placement"}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {activePlacement ? "Currently housed" : "Awaiting placement"}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Total Placements</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{placements.length}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Historical placements
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Property Matches</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {matchedProperties.length}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Based on preferences
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>Common tasks and resources</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-3">
              <Button
                variant="outline"
                className="justify-start h-auto p-4"
                onClick={() => navigate("/property-search")}
              >
                <div className="flex items-start gap-3 text-left">
                  <MapPin className="h-5 w-5 mt-0.5" />
                  <div>
                    <div className="font-semibold">Browse Properties</div>
                    <div className="text-xs text-muted-foreground">View available housing</div>
                  </div>
                </div>
              </Button>
              <Button
                variant="outline"
                className="justify-start h-auto p-4"
                onClick={() => {
                  const element = document.getElementById('preferences-form');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <div className="flex items-start gap-3 text-left">
                  <Sparkles className="h-5 w-5 mt-0.5" />
                  <div>
                    <div className="font-semibold">Update Preferences</div>
                    <div className="text-xs text-muted-foreground">Refine your matches</div>
                  </div>
                </div>
              </Button>
              <Button
                variant="outline"
                className="justify-start h-auto p-4"
                onClick={() => navigate("/dashboard")}
              >
                <div className="flex items-start gap-3 text-left">
                  <AlertCircle className="h-5 w-5 mt-0.5" />
                  <div>
                    <div className="font-semibold">Contact Support</div>
                    <div className="text-xs text-muted-foreground">Get assistance</div>
                  </div>
                </div>
              </Button>
            </CardContent>
          </Card>

          {activePlacement ? (
            <Card className="border-primary">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-2xl">Current Placement</CardTitle>
                  <Badge variant="default" className="text-sm">Active</Badge>
                </div>
                <CardDescription>Your current housing arrangement</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 mt-0.5 text-primary" />
                      <div>
                        <h3 className="font-semibold">{activePlacement.properties.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {activePlacement.properties.address}<br />
                          {activePlacement.properties.city}, {activePlacement.properties.state} {activePlacement.properties.zip}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Home className="h-5 w-5 mt-0.5 text-primary" />
                      <div>
                        <h3 className="font-semibold">Property Details</h3>
                        <p className="text-sm text-muted-foreground">
                          {activePlacement.properties.bedrooms} Bedrooms, {activePlacement.properties.bathrooms} Bathrooms<br />
                          Monthly Rent: ${activePlacement.properties.rent_amount}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Calendar className="h-5 w-5 mt-0.5 text-primary" />
                      <div>
                        <h3 className="font-semibold">Placement Period</h3>
                        <p className="text-sm text-muted-foreground">
                          Start: {new Date(activePlacement.start_date).toLocaleDateString()}<br />
                          End: {activePlacement.end_date ? new Date(activePlacement.end_date).toLocaleDateString() : "Ongoing"}
                        </p>
                      </div>
                    </div>

                    {activePlacement.notes && (
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 mt-0.5 text-primary" />
                        <div>
                          <h3 className="font-semibold">Notes</h3>
                          <p className="text-sm text-muted-foreground">{activePlacement.notes}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <Home className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Active Placement</h3>
                  <p className="text-muted-foreground">
                    You don't have an active placement at the moment. Contact your coordinator for more information.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Placement History</CardTitle>
              <CardDescription>Your previous and upcoming placements</CardDescription>
            </CardHeader>
            <CardContent>
              {placements.length > 0 ? (
                <div className="space-y-4">
                  {placements.map((placement) => (
                    <div
                      key={placement.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex-1">
                        <h4 className="font-semibold">{placement.properties.name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {placement.properties.city}, {placement.properties.state}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {new Date(placement.start_date).toLocaleDateString()} - {" "}
                          {placement.end_date ? new Date(placement.end_date).toLocaleDateString() : "Ongoing"}
                        </p>
                      </div>
                      <Badge variant={getStatusBadge(placement.status)}>
                        {getStatusLabel(placement.status)}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No placement history available</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Client Preferences */}
          <div id="preferences-form">
            <ClientPreferencesForm userId={effectiveUserId!} />
          </div>

          {/* Matched Properties */}
          {matchedProperties.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <CardTitle>Recommended Properties</CardTitle>
                </div>
                <CardDescription>
                  Properties matching your preferences
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {matchedProperties.map((property) => (
                    <Card key={property.id}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <h3 className="font-semibold">{property.name}</h3>
                          <Badge variant="default">
                            Match: {property.matchScore}
                          </Badge>
                        </div>
                        <div className="flex items-start gap-2 text-sm text-muted-foreground">
                          <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <div>
                            <p>{property.address}</p>
                            <p>{property.city}, {property.state} {property.zip}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Bedrooms:</span>
                            <span className="font-medium ml-1">{property.bedrooms}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Bathrooms:</span>
                            <span className="font-medium ml-1">{property.bathrooms}</span>
                          </div>
                        </div>
                        <div className="pt-2 border-t">
                          <p className="text-lg font-bold text-primary">
                            ${property.rent_amount.toLocaleString()}/mo
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ClientDashboard;
